import { mapActions, mapGetters } from 'vuex';
import { has, get } from 'lodash-es';

export default {
  computed: {
    ...mapGetters('graphicsTemplate', [
      'getElementParentGroupId',
      'getDataValue',
      'isPropertyMapped',
      'getDataProvider',
      'isDataProvider',
      'getElementById',
    ]),
    parent() {
      return this.getElementParentGroupId(this.element.id);
    },
    dataProvider() {
      if (this.isDataProvider(this.element.id)) {
        return this.element;
      }
      return this.getDataProvider(this.element.id);
    },
    showDataMapControls() {
      // TODO: remove sheets check when they can have more than one thing mapped
      if (this.editingMultiple) {
        return this.dataProvider && this.hasSameDataProvider && this.dataProvider?.type !== 'sheet-data';
      }

      return this.dataProvider && this.dataProvider?.type !== 'sheet-data';
    },
    isBackgroundColorMapped() {
      return this.checkMapped('backgroundColor')
        || this.checkMapped('gradientColorOne')
        || this.checkMapped('gradientColorTwo');
    },
    selectedElements() {
      return this.$store.state.templates.selectedElements;
    },
    editingMultiple() {
      return this.selectedElements.length > 1;
    },
    hasSameDataProvider() {
      if (!this.editingMultiple) return false;

      return this.selectedElements
        .every((id) => this.getDataProvider(id)?.id === this.dataProvider?.id);
    },
    labelOverride() {
      return this.dataProvider?.type === 'sportradar' && this.element.data?.isLabel;
    },
  },
  methods: {
    ...mapActions('graphicsTemplate', [
      'setOneElementValueByProp',
      'setManyElementValuesByProp',
      'deleteManyElementProps',
      'incrementElementPosition',
      'decrementElementPosition',
      'onMoveOrResize',
      'deleteOneElementProp',
    ]),
    incrementPosition(positionType, stepSize = 1, id = this.element.id) {
      this.incrementElementPosition({
        value: { id, positionType, stepSize },
      });
    },
    decrementPosition(positionType, stepSize = 1, id = this.element.id) {
      this.decrementElementPosition({
        value: { id, positionType, stepSize },
      });
    },

    getPropertyValue(path, { mappedPath = null, emptyValue = null, fallbackValue = null } = {}) {
      if (this.editingMultiple) {
        const compareValue = get(this.element, path) ?? fallbackValue;
        return this.selectedElements
          .every((id) => compareValue === get(this.getElementById(id), path))
          ? compareValue : emptyValue;
      }

      return (mappedPath && this.checkMapped(mappedPath) && this.dataValue(mappedPath))
        || (get(this.element, path) ?? fallbackValue);
    },

    multipleElementEdit(params, options, cb) {
      this.selectedElements.forEach((id, index) => {
        const opts = {
          ...options,
          blacklist: index === this.selectedElements.length - 1, // undo/redo, only "save" on last
        };
        cb(...params, id, opts, true);
      });
    },

    updateOneElementValue(propKey, propValue, id = this.element.id, options = {}, skipCheck = false) {
      if (this.editingMultiple && !skipCheck) {
        this.multipleElementEdit([propKey, propValue], options, this.updateOneElementValue);
      } else {
        this.setOneElementValueByProp({
          value: {
            id,
            propKey,
            propValue,
          },
          options,
        });
      }
    },
    updateManyElementValues(pathValueMap, id = this.element.id, options = {}, skipCheck = false) {
      if (this.editingMultiple && !skipCheck) {
        this.multipleElementEdit([pathValueMap], options, this.updateManyElementValues);
      } else {
        this.setManyElementValuesByProp({
          value: {
            id,
            pathValueMap,
          },
          options,
        });
      }
    },
    removeOneElementProp(propKey, id = this.element.id, options = {}, skipCheck = false) {
      if (this.editingMultiple && !skipCheck) {
        this.multipleElementEdit([propKey], options, this.removeOneElementProp);
      } else {
        this.deleteOneElementProp({
          value: {
            id,
            propKey,
          },
          options,
        });
      }
    },
    removeManyElementProps(propPaths, id = this.element.id, options = {}, skipCheck = false) {
      if (this.editingMultiple && !skipCheck) {
        this.multipleElementEdit([propPaths], options, this.removeManyElementProps);
      } else {
        this.deleteManyElementProps({
          value: {
            id,
            propPaths,
          },
          options,
        });
      }
    },

    checkMapped(property) {
      if (this.editingMultiple) {
        return this.selectedElements.some((id) => this.isPropertyMapped(id, property));
      }
      return this.isPropertyMapped(this.element.id, property);
    },
    // TODO: remove isMapped when all converted to checkMapped
    isMapped(property) {
      return this.isPropertyMapped(this.element.id, property);
    },
    dataValue(property) {
      if (this.isMapped(property)) {
        return this.getDataValue(this.element.id, property);
      }
      return null;
    },

    mapToField({ path, value, property }) {
      // set provider in case the element was dragged in or something
      this.updateOneElementValue('data.provider', this.dataProvider.id);

      // cleanup old sheets that could only have one property mapped
      if (has(this.element, 'data.columnIdx')
      && ['text', 'backgroundImageUrl'].includes(property)) {
        this.removeOneElementProp('data.columnIdx');
      }

      this.updateOneElementValue(path, value);

      if (has(this.element, 'data.linkedElement')) {
        if (property === 'text') {
          this.updateOneElementValue(path, value, this.element.data.linkedElement);
        }
      }

      if (this.dataProvider.type === 'sportradar') {
        if (property === 'color') {
          return; // Don't override label name for color mappings
        }

        // set new label/name to last part of data path
        const name = value.split('.').pop().split('_')
          .map((word) => word[0].toUpperCase() + word.substring(1))
          .join(' ');

        if (has(this.element, 'data.dataKeyMap')) {
          // set new label/name to the linked element
          this.updateOneElementValue('name', name, this.element.data.linkedElement);
        }

        this.updateOneElementValue('name', name);
      }
    },

    clearMapping(property) {
      this.updateOneElementValue(`data.dataKeyMap.${property}`, null);
      // handle old sheets
      if (has(this.element, 'data.columnIdx')) {
        this.removeOneElementProp('data.columnIdx');
      }
      if (has(this.element, 'data.dataValue')) {
        this.removeOneElementProp('data.dataValue');
      }

      // TODO: unlink when clearing?
    },
  },
};
